<script setup lang="ts">
import { mergeRegister } from "@lexical/utils";
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_NORMAL, KEY_BACKSPACE_COMMAND } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import { $isTypeaheadNode } from "~/components/text/nodes/TypeaheadNode";
import { useDataStore } from "~/stores";

const props = defineProps<{
  chatDuid?: string;
  skillDuid?: string;
  isEmpty: boolean;
}>();

const dataStore = useDataStore();
const editor = useLexicalComposer();

let unregisterListeners: () => void;

const onBackspace = (event: KeyboardEvent) => {
  const chat = dataStore.getChatByDuidIfLoaded(props.chatDuid ?? "");
  const skill = dataStore.getSkillByDuid(props.skillDuid ?? "");
  if (!chat && !skill) {
    return false;
  }

  const selection = $getSelection();
  if (!props.isEmpty || (selection && $isRangeSelection(selection) && $isTypeaheadNode(selection.anchor.getNode()))) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();

  if (skill) {
    dataStore.updateSkill({
      duid: skill.duid,
      attachmentDuids: skill.attachmentDuids.slice(0, -1),
    });

    return true;
  }

  if (!chat) {
    return false;
  }

  const draft = dataStore.getDraftMessageByChatDuid(chat.duid);
  if (!draft) {
    return false;
  }

  const { extras, attachmentDuids } = draft;

  // attachments are visually ordered to be the last in the context
  // so we remove attachmentDuids first, then extras
  dataStore.updateChatDraftMessage(chat.duid, {
    ...draft,
    attachmentDuids: draft.attachmentDuids.slice(0, -1),
    extras: attachmentDuids.length > 0 ? extras : extras.slice(0, -1),
  });

  return true;
};

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand(KEY_BACKSPACE_COMMAND, onBackspace, COMMAND_PRIORITY_NORMAL)
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
